import { useCallback, useEffect, useRef, useState } from 'react';
import { useEvents, useHasQueuedEvents } from '~/shared/hooks/useEvents';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useRouter } from 'next/router';
import type { Frame } from '../utils';
import type { Pages } from '~/lib';
import CryptoJS from 'crypto-js';

type Props = {
    frame: Frame;
    page: Pages;
};

const isServer = typeof window === 'undefined';
const pagesExludedFromPageView = ['checkout'];

export function useGoogleTagManagerScript({ frame, page }: Props) {
    if (isServer) return null;

    const isInitialized = useRef(false);
    const router = useRouter();
    const events = useEvents(frame);
    const hasQueue = useHasQueuedEvents();
    const customer = useCustomer();
    const url = useRef<string>(window.location.pathname);
    const previousUrl = useRef<string>();
    const [currentPath, setCurrentPath] = useState(url.current);
    const hasTriggeredUserData = useRef(false);

    const handleUrlChange = useCallback((newUrl: string) => {
        previousUrl.current = url.current;
        url.current = newUrl;
        setCurrentPath(newUrl);
    }, []);

    async function handlePageView() {
        const sha256email = customer?.email
            ? CryptoJS.SHA256(customer?.email).toString()
            : undefined;
        // TEMP: Remove when we have a proper solution for this
        if (pagesExludedFromPageView.includes(url.current)) return;

        if (previousUrl.current !== url.current) {
            events.pageView({
                page,
                loggedIn: !customer.isGuest,
                customerId: sha256email,
            });
            previousUrl.current = url.current;
        }
    }

    async function handleUserData() {
        if (pagesExludedFromPageView.includes(url.current)) return;

        events.userData({
            user_id: customer.c_imercoCustomerNo,
            email: customer.email,
            phone: customer.phoneMobile,
            first_name: customer.firstName,
            last_name: customer.lastName,
            street: customer.addresses?.[0].address1,
            city: customer.addresses?.[0].city,
            country: customer?.addresses?.[0]?.countryCode,
            postal_code: customer.addresses?.[0].postalCode,
        });
    }

    useEffect(() => {
        router.events.on('routeChangeComplete', handleUrlChange);

        return () => {
            router.events.off('routeChangeComplete', handleUrlChange);
        };
    }, [router.events]);

    useEffect(() => {
        if (!customer.isInitialized || isInitialized.current || events.isReady) return;

        events.initialize(customer.customerId, customer.c_imercoCustomerNo);
    }, [customer.customerId, customer.c_imercoCustomerNo, customer.isInitialized, events.isReady]);

    useEffect(() => {
        if (!events.isReady) return;

        handlePageView();

        if (hasQueue) {
            events.runQueue();
        }
    }, [events.isReady, currentPath, hasQueue]);

    useEffect(() => {
        if (!events.isReady) return;

        if (!hasTriggeredUserData.current && customer.isRegistered && customer.c_imercoCustomerNo) {
            hasTriggeredUserData.current = true;
            handleUserData();
        }
    }, [customer.isRegistered, customer.c_imercoCustomerNo, events]);
}
